import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
import CandidateTableRow from './CandidateTableRow';
import {
  LAST_CHECK_SORT_ASC,
  LAST_CHECK_SORT_DEFAULT,
  LAST_CHECK_SORT_DESC,
  cycleLastCheckSort,
} from '../../../lib/utils/candidateListSort';

const CandidatesTable = ({
  candidates,
  onTogglePriority,
  onDeleteCandidate,
  userPlan,
  isRestricted,
  lastCheckSort = LAST_CHECK_SORT_DEFAULT,
  onLastCheckSortChange,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  totalFilteredCount = 0,
}) => {
  const { t } = useTranslation();
  const pageIds = useMemo(
    () => (candidates || []).map((c) => c?.id).filter(Boolean),
    [candidates],
  );
  const selectedOnPage = pageIds.filter((id) => selectedIds?.has(id));
  const allPageSelected = pageIds.length > 0 && selectedOnPage.length === pageIds.length;
  const somePageSelected = selectedOnPage.length > 0 && !allPageSelected;
  const allCandidatesSelected = totalFilteredCount > 0
    && selectedIds?.size === totalFilteredCount
    && pageIds.every((id) => selectedIds?.has(id));
  const headerChecked = allCandidatesSelected;
  const headerIndeterminate = !allCandidatesSelected && (allPageSelected || somePageSelected);
  const headerAriaLabel = allCandidatesSelected
    ? t('candidates.clearSelection')
    : allPageSelected
      ? t('candidates.selectAll')
      : t('candidates.selectAllPage');

  if (candidates?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <p className="text-muted-foreground">{t('candidates.noMatch')}</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="pl-4 pr-2 py-3 w-8 text-left">
                <Checkbox
                  id="select-all-candidates"
                  size="sm"
                  checked={headerChecked}
                  indeterminate={headerIndeterminate}
                  onChange={() => onToggleSelectAll?.(pageIds)}
                  aria-label={headerAriaLabel}
                  className="justify-center space-x-0"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {t('candidates.tableCandidate')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {t('candidates.tableCompany')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                {t('candidates.tableStatus')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                <span className="inline-flex items-center gap-1.5">
                  {t('candidates.tableLastCheck')}
                  <button
                    type="button"
                    onClick={() => onLastCheckSortChange?.(cycleLastCheckSort(lastCheckSort))}
                    className="inline-flex text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={t('candidates.sortLastCheck')}
                  >
                    {lastCheckSort === LAST_CHECK_SORT_DESC && (
                      <Icon name="ArrowDown" size={14} />
                    )}
                    {lastCheckSort === LAST_CHECK_SORT_ASC && (
                      <Icon name="ArrowUp" size={14} />
                    )}
                    {lastCheckSort === LAST_CHECK_SORT_DEFAULT && (
                      <Icon name="ArrowUpDown" size={14} className="opacity-60" />
                    )}
                  </button>
                </span>
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                {t('candidates.tableContact')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {t('candidates.tableActions')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {candidates?.map((candidate) => (
              <CandidateTableRow
                key={candidate?.id}
                candidate={candidate}
                onTogglePriority={onTogglePriority}
                onDeleteCandidate={onDeleteCandidate}
                userPlan={userPlan}
                isRestricted={isRestricted}
                isSelected={selectedIds?.has(candidate?.id)}
                onToggleSelect={onToggleSelect}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CandidatesTable;